import { React, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Layout, ConfigProvider } from 'antd';
import 'antd/dist/reset.css'; // Import Ant Design styles
import './styles/custom.css'; // LeadLens custom CSS file
import './components/analytics/reportExportStyles.css';

import NavBar from './components/NavBar'; // Assume NavBar is in the components folder
import Dashboard from './components/Dashboard'; // Dashboard component
import { Alert } from 'antd';
import { useSharedData } from "./components/contexts/SharedDataContext";
import CallAuditView from "./components/experiments/CallAuditView";
import CallEvaluationView from "./components/experiments/CallEvaluationView";
import AnalyticsView from "./components/analytics/AnalyticsView";
import { ModalProvider } from "./components/analytics/ModalContext";
import { RBAC_ROLES } from './hooks/useRBAC';
import SecureComponent from './components/SecureComponent';
import SecureRoute from './components/SecureRoute';
import SmartRulesView from './components/rules/SmartRulesView';

const { Content, Footer } = Layout;

const App = () => {
    const { isLoading, isAuthenticated, loginWithRedirect, error } = useAuth0();
    const [selectedCompanyNames, setSelectedCompanyNames] = useState([]);
    const { selectedTenant, showNotification, lastMessage } = useSharedData();

    const handleCompanyNamesChange = (selectedCompanyNames) => {
        if(selectedCompanyNames != null && !(selectedCompanyNames instanceof Array)) {
            selectedCompanyNames = [selectedCompanyNames];
        }
        else {
            setSelectedCompanyNames(selectedCompanyNames);
        }
    };

    const applyTheme = (themeConfig) => {
        ConfigProvider.config({ theme: themeConfig });
    };
    window.applyTheme = applyTheme;

    useEffect(() => {
        if (!isLoading && !isAuthenticated && !error) {
            loginWithRedirect();
        }
    }, [isLoading, isAuthenticated, loginWithRedirect, error]);

    const AuthCallbackHandler = () => {
        const { isLoading, isAuthenticated, error, user } = useAuth0();

        useEffect(() => {
            console.log('Auth Handler Called');
            console.log('Loading = ' + isLoading);
            console.log('Authenticated = ' + isAuthenticated);
            console.log('User:');
            console.dir(user);
            console.log('Error:');
            console.dir(error);

            if (!isLoading && isAuthenticated) {
                // Redirect to home or dashboard after successful authentication
                window.location.href = '/';
            }
            // Handle error or loading states as needed
        }, [isLoading, isAuthenticated, error]);

        return <div>Loading...</div>; // Display a loading message or spinner
    };

    return (
        <ConfigProvider theme={require('./styles/theme.json')}>
            <Router key={selectedTenant}>
                <Layout style={{ minHeight: '100vh' }}>
                    <NavBar onCompanyNamesChange={handleCompanyNamesChange} />
                    <Layout className="site-layout">
                        <Content style={{ margin: '0 16px', overflowY: 'scroll', height: 'calc(100vh - 70px - 16px - 16px)' }}>
                            {showNotification && lastMessage && (
                                <Alert
                                    message="New Call Notification"
                                    description={`A new call has been received: ${lastMessage.callId}`}
                                    type="info"
                                    closable
                                />
                            )}
                            <Routes>
                                <Route path="/auth-callback" element={<AuthCallbackHandler />} />
                                <Route path="/" element={isAuthenticated ? <div /> : <div />} />
                                {/* Analyst routes */}
                                <Route
                                    path="/analytics"
                                    element={
                                        <SecureRoute
                                            element={<ModalProvider>
                                                <AnalyticsView selectedCompanyNames={selectedCompanyNames} />
                                            </ModalProvider>}
                                            requiredRoles={RBAC_ROLES.ANY_STAFF}
                                        />
                                    }
                                />

                                <Route
                                    path="/smart-rules"
                                    element={
                                        <SecureRoute
                                            element={
                                                <SmartRulesView />
                                            }
                                            requiredRoles={RBAC_ROLES.SUPER_ADMIN}
                                        />
                                    }
                                />

                                {/* Regular user routes */}
                                <Route
                                    path="/calls"
                                    element={
                                        <SecureRoute
                                            element={<Dashboard view="Calls" selectedCompanyNames={selectedCompanyNames} />}
                                            requiredRoles={RBAC_ROLES.DEV_ONLY}
                                        />
                                    }
                                />

                                {/* Admin-only routes */}
                                <Route
                                    path="/agents"
                                    element={
                                        <SecureRoute
                                            element={<Dashboard view="Agents" selectedCompanyNames={selectedCompanyNames} />}
                                            requiredRoles={RBAC_ROLES.DEV_ONLY}
                                        />
                                    }
                                />

                                {/* Dev/test routes */}
                                <Route
                                    path="/live-calls"
                                    element={
                                        <SecureRoute
                                            element={<CallAuditView />}
                                            requiredRoles={RBAC_ROLES.DEV_ONLY}
                                        />
                                    }
                                />

                                <Route
                                    path="/test-configuration"
                                    element={
                                        <SecureRoute
                                            element={<CallEvaluationView />}
                                            requiredRoles={RBAC_ROLES.SUPER_ADMIN}
                                        />
                                    }
                                />

                                {/* Unauthorized route */}
                                <Route path="/unauthorized" element={<div>You don't have permission to access this page.</div>} />
                            </Routes>
                        </Content>
                        <Footer style={{ textAlign: 'center' }}>LeadLensAI ©2024 created by All Points Digital</Footer>
                    </Layout>
                </Layout>
            </Router>
        </ConfigProvider>
    );
};

export default App;
